<template>
  <div class="matches-carousel">
    <div class="carousel-header">
      <h2 class="carousel-title">{{ title }}</h2>
      <div class="carousel-controls">
        <button 
          class="nav-btn nav-btn--prev" 
          @click="slidePrev"
          :disabled="currentIndex === 0"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <button 
          class="nav-btn nav-btn--next" 
          @click="slideNext"
          :disabled="currentIndex >= maxIndex"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="carousel-wrapper">
      <div class="carousel-container" ref="carouselContainer">
        <div 
          class="carousel-track" 
          :style="trackStyle"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div 
            v-for="match in matches" 
            :key="match.id"
            class="match-card"
            :style="cardStyle"
            :class="{ 
              'match-card--live': match.isLive,
              'match-card--highlighted': match.highlighted 
            }"
          >
            <!-- Live Badge -->
            <div v-if="match.isLive" class="live-badge">
              <span class="live-dot"></span>
              LIVE
            </div>

            <!-- League Info -->
            <div class="match-league">
              <img 
                v-if="match.competition.logo" 
                :src="match.competition.logo" 
                :alt="match.competition.name" 
                class="league-logo"
              >
              <span class="league-name">{{ match.competition.name }}</span>
            </div>

            <!-- Match Time -->
            <div class="match-time">
              <span v-if="!match.isLive">{{ formatMatchTime(match.startedAt) }}</span>
              <span v-else class="match-minute">{{ getMatchMinute(match) }}'</span>
            </div>

            <!-- Teams and Score -->
            <div class="match-teams">
              <div class="team">
                <img 
                  v-if="match.home.logo" 
                  :src="match.home.logo" 
                  :alt="match.home.name" 
                  class="team-logo"
                >
                <span class="team-name">{{ match.home.name }}</span>
                <span v-if="match.isLive" class="team-score">{{ getScore(match).home }}</span>
              </div>
              <div class="team">
                <img 
                  v-if="match.away.logo" 
                  :src="match.away.logo" 
                  :alt="match.away.name" 
                  class="team-logo"
                >
                <span class="team-name">{{ match.away.name }}</span>
                <span v-if="match.isLive" class="team-score">{{ getScore(match).away }}</span>
              </div>
            </div>

            <!-- Betting Odds -->
            <div class="match-odds" v-if="getMainMarket(match)">
              <button 
                v-for="selection in getMainMarketSelections(match)"
                :key="selection.id"
                class="odd-btn" 
                @click="selectOdd(match.id, selection)"
                :class="{ 
                  'odd-btn--selected': isOddSelected(match.id, selection.id),
                  'odd-btn--disabled': selection.isDisabled 
                }"
                :disabled="selection.isDisabled"
              >
                <span class="odd-label">{{ getOddLabel(selection.outcome) }}</span>
                <span class="odd-value">{{ selection.value }}</span>
              </button>
            </div>

            <!-- More Markets -->
            <button class="more-markets-btn" @click="viewMoreMarkets(match.id)">
              +{{ match.marketCount }} Markets
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination Dots -->
    <div class="carousel-dots">
      <button 
        v-for="(dot, index) in totalDots" 
        :key="index"
        class="dot"
        :class="{ 'dot--active': currentDotIndex === index }"
        @click="goToSlide(index * actualSlidesToShow)"
      ></button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HighlightMatchesCarousel',
  
  props: {
    title: {
      type: String,
      default: 'Highlight Matches'
    },
    matches: {
      type: Array,
      required: true,
      default: function() { return [] }
    },
    slidesToShow: {
      type: Number,
      default: 3
    },
    slidesToShowTablet: {
      type: Number,
      default: 2
    },
    slidesToShowMobile: {
      type: Number,
      default: 1
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    autoplayInterval: {
      type: Number,
      default: 5000
    },
    mainMarketType: {
      type: Number,
      default: 25202
    },
    gap: {
      type: Number,
      default: 16
    }
  },

  data: function() {
    return {
      currentIndex: 0,
      selectedOdds: {},
      touchStartX: 0,
      touchEndX: 0,
      autoplayTimer: null,
      windowWidth: 0,
      containerWidth: 0
    }
  },

  computed: {
    actualSlidesToShow: function() {
      if (this.windowWidth <= 640) {
        return this.slidesToShowMobile
      } else if (this.windowWidth <= 1024) {
        return this.slidesToShowTablet
      }
      return this.slidesToShow
    },
    
    maxIndex: function() {
      return Math.max(0, this.matches.length - this.actualSlidesToShow)
    },
    
    totalDots: function() {
      return Math.ceil(this.matches.length / this.actualSlidesToShow)
    },
    
    currentDotIndex: function() {
      return Math.floor(this.currentIndex / this.actualSlidesToShow)
    },
    
    cardWidth: function() {
      if (this.containerWidth === 0) return 300
      var totalGaps = this.gap * (this.actualSlidesToShow - 1)
      return (this.containerWidth - totalGaps) / this.actualSlidesToShow
    },
    
    cardStyle: function() {
      return {
        width: this.cardWidth + 'px',
        flex: '0 0 ' + this.cardWidth + 'px'
      }
    },
    
    trackStyle: function() {
      var translateX = -(this.currentIndex * (this.cardWidth + this.gap))
      return {
        transform: 'translateX(' + translateX + 'px)',
        transition: 'transform 0.3s ease-in-out',
        gap: this.gap + 'px'
      }
    }
  },

  mounted: function() {
    this.updateDimensions()
    window.addEventListener('resize', this.handleResize)
    
    if (this.autoplay) {
      this.startAutoplay()
    }
  },

  beforeDestroy: function() {
    window.removeEventListener('resize', this.handleResize)
    this.stopAutoplay()
  },

  methods: {
    updateDimensions: function() {
      this.windowWidth = window.innerWidth
      if (this.$refs.carouselContainer) {
        this.containerWidth = this.$refs.carouselContainer.offsetWidth
      }
    },
    
    handleResize: function() {
      this.updateDimensions()
      // Adjust current index if needed after resize
      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex
      }
    },
    
    slideNext: function() {
      if (this.currentIndex < this.maxIndex) {
        var nextIndex = this.currentIndex + this.actualSlidesToShow
        this.currentIndex = Math.min(nextIndex, this.maxIndex)
      }
    },
    
    slidePrev: function() {
      if (this.currentIndex > 0) {
        var prevIndex = this.currentIndex - this.actualSlidesToShow
        this.currentIndex = Math.max(prevIndex, 0)
      }
    },
    
    goToSlide: function(index) {
      this.currentIndex = Math.min(index, this.maxIndex)
    },
    
    handleTouchStart: function(e) {
      this.touchStartX = e.touches[0].clientX
      this.stopAutoplay()
    },
    
    handleTouchMove: function(e) {
      this.touchEndX = e.touches[0].clientX
    },
    
    handleTouchEnd: function() {
      var swipeDistance = this.touchStartX - this.touchEndX
      var threshold = 50
      
      if (Math.abs(swipeDistance) > threshold) {
        if (swipeDistance > 0) {
          this.slideNext()
        } else {
          this.slidePrev()
        }
      }
      
      if (this.autoplay) {
        this.startAutoplay()
      }
    },
    
    startAutoplay: function() {
      var self = this
      this.autoplayTimer = setInterval(function() {
        if (self.currentIndex >= self.maxIndex) {
          self.currentIndex = 0
        } else {
          self.slideNext()
        }
      }, this.autoplayInterval)
    },
    
    stopAutoplay: function() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer)
        this.autoplayTimer = null
      }
    },
    
    formatMatchTime: function(timestamp) {
      var date = new Date(timestamp)
      var hours = date.getHours().toString().padStart(2, '0')
      var minutes = date.getMinutes().toString().padStart(2, '0')
      return hours + ':' + minutes
    },
    
    getScore: function(match) {
      if (match.data) {
        try {
          var data = JSON.parse(match.data)
          return data.score || { home: '0', away: '0' }
        } catch (e) {
          return { home: '0', away: '0' }
        }
      }
      return { home: '0', away: '0' }
    },
    
    getMatchMinute: function(match) {
      return match.periodTime || '0'
    },
    
    getMainMarket: function(match) {
      var self = this
      if (!match.markets) return null
      return match.markets.find(function(m) {
        return m.t === self.mainMarketType && m.isMain
      })
    },
    
    getMainMarketSelections: function(match) {
      var market = this.getMainMarket(match)
      if (!market || !market.selections) return []
      
      return market.selections.sort(function(a, b) {
        return a.order - b.order
      })
    },
    
    getOddLabel: function(outcome) {
      var labels = {
        '1': '1',
        'X': 'X',
        '2': '2',
        'home': '1',
        'draw': 'X',
        'away': '2'
      }
      return labels[outcome] || outcome
    },
    
    selectOdd: function(matchId, selection) {
      var key = matchId + '-' + selection.id
      
      if (this.selectedOdds[key]) {
        this.$delete(this.selectedOdds, key)
      } else {
        this.$set(this.selectedOdds, key, {
          matchId: matchId,
          marketId: selection.typeId,
          selectionId: selection.id,
          outcome: selection.outcome,
          value: selection.value
        })
      }
      
      this.$emit('odd-selected', {
        matchId: matchId,
        selection: selection,
        isSelected: !!this.selectedOdds[key]
      })
    },
    
    isOddSelected: function(matchId, selectionId) {
      var key = matchId + '-' + selectionId
      return !!this.selectedOdds[key]
    },
    
    viewMoreMarkets: function(matchId) {
      this.$emit('view-more-markets', matchId)
    }
  }
}
</script>

<style scoped>
.matches-carousel {
  width: 99.9vw;
  padding: 20px 0;
  border-radius: 12px;
  overflow: hidden;
}

.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 20px;
}

.carousel-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.carousel-controls {
  display: flex;
  gap: 8px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}

.nav-btn:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #999;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.carousel-wrapper {
  padding: 0 10px;
}

.carousel-container {
  width: 100%;
}

.carousel-track {
  display: flex;
}

.match-card {
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  background: linear-gradient(270deg, rgba(56, 56, 56, 1) 1%, rgba(7, 5, 51, 0) 100%);
  border: 1px solid #606060;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.match-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.match-card--live {
  border: 2px solid #ff4444;
}

.match-card--highlighted {
  background: linear-gradient(135deg, #fff 0%, #fffbf0 100%);
  border: 1px solid #ffd700;
}

.live-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #ff4444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
}

.live-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.match-league {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.league-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.league-name {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.match-time {
  text-align: center;
  margin-bottom: 16px;
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.match-minute {
  color: #ff4444;
}

.match-teams {
  margin-bottom: 16px;
}

.team {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.team-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.team-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--black);
}

.team-score {
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.match-odds {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.odd-btn {
  flex: 1;
  padding: 8px 4px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: var(--white);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.odd-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #999;
}

.odd-btn--selected {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.odd-btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.odd-label {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.7;
}

.odd-value {
  font-size: 14px;
  font-weight: 700;
}

.more-markets-btn {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.more-markets-btn:hover {
  background: #e8e9ea;
  color: #333;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: #d0d0d0;
  cursor: pointer;
  transition: all 0.2s;
}

.dot--active {
  background: #4CAF50;
  width: 24px;
  border-radius: 4px;
}
</style>


// WEBPACK FOOTER //
// src/components/widget/highlightEventCarousel.vue